// Modules
import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'

// Hooks
import useLogin from 'hooks/authentication/useLogin'
import useNotices from 'hooks/system/useNotices'

// Constants
import { REGISTRATION_URL, THIRD_PARTY_AUTHENTICATION_URL } from 'constants/environment'

// Hook: Authentication > Register
function useRegister () {
	// State
	const [registrationError, setRegistrationError] = useState(null)
	const [isProcessing, setIsProcessing] = useState(false)

	// Data
	const { addNotice } = useNotices()
	const { error: loginError, login } = useLogin()

	// Function (Memoized)
	const registerUsingDefaultMethod = useCallback(async (payload, parameters) => {
		try {
			const response = await axios({
				...(parameters || {}),
				data: payload,
				method: 'POST',
				url: REGISTRATION_URL
			})
			if (response.status < 300) {
				window.dataLayer.push({ event: 'account-registration', type: 'default' })
				await login({ email: payload.email, password: payload.password })
			}
		} catch (error) {
			setRegistrationError(error)
		}
	}, [login])
	const registerUsingGoogle = useCallback(async (payload, parameters) => {
		try {
			const response = await axios({
				...(parameters || {}),
				data: { ...payload, credentials: payload.credential },
				method: 'POST',
				url: `${THIRD_PARTY_AUTHENTICATION_URL}/google/register`
			})
			if (response.status < 300) {
				window.dataLayer.push({ event: 'account-registration', type: 'google' })
				await login({ ...payload, credentials: payload.credential }, 'google')
			}
		} catch (error) {
			setRegistrationError(error)
		}
	}, [login])
	const registerUsingApple = useCallback(async (payload, parameters) => {
		try {
			const response = await axios({
				...(parameters || {}),
				data: {
					...payload,
					credentials: payload?.authorization?.id_token,
					firstName: payload?.user?.name?.firstName,
					lastName: payload?.user?.name?.lastName
				},
				method: 'POST',
				url: `${THIRD_PARTY_AUTHENTICATION_URL}/apple/register`
			})
			if (response.status < 300) {
				window.dataLayer.push({ event: 'account-registration', type: 'apple' })
				await login({ ...payload, credentials: payload.credential, firstName: payload?.user?.name?.firstName, lastName: payload?.user?.name?.lastName }, 'apple')
			}
		} catch (error) {
			setRegistrationError(error)
		}
	}, [login])
	const register = useCallback(async (payload, { parameters, service } = {}) => {
		setIsProcessing(true)
		switch (service) {
			case 'apple':
				await registerUsingApple(payload, parameters)
				break
			case 'google':
				await registerUsingGoogle(payload, parameters)
				break
			default:
				await registerUsingDefaultMethod(payload, parameters)
		}
		setIsProcessing(false)
	}, [registerUsingDefaultMethod, registerUsingApple, registerUsingGoogle])

	// Effects
	useEffect(() => {
		if (!!registrationError) {
			let errorMessage = registrationError.response?.data?.message?.[0]
			if (!!errorMessage) addNotice({ code: `server.${errorMessage}`, log: registrationError.response.data, type: 'error' })
			else addNotice({ code: 'client.registrationError', type: 'error' })
		}
	}, [addNotice, registrationError])

	// Response
	return { error: registrationError || loginError, isProcessing, register }
}

// Export
export default useRegister
